import React, { useState, useEffect, useRef, useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useChatAI } from "../chat-ai-context";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import { DatePicker } from "@/components/application/date-picker/date-picker";
import { Avatar } from "@/components/base/avatar/avatar";
import { orderService } from "@/data/mock-orders";
import { collaborationService } from "@/data/mock-collaborations";
import { accountService } from "@/data/mock-accounts";
import { getWeekNumber } from "@/utils/week-utils";
import { today, getLocalTimeZone, CalendarDate } from "@internationalized/date";
import { storage } from "@/lib/local-storage";

interface OrderWizardProps {
    onCancel: () => void;
}

type Step = "shipperId" | "targetDate" | "packingListRequested" | "notes" | "review" | "completed";

// Componente memoizzato per i messaggi dell'assistente
const AssistantMessage = memo(({ question, avatar }: { question: string; avatar: React.ReactElement }) => {
    return (
        <div className="flex items-start">
            {avatar}
            <div className="flex-1">
                <div className="max-w-[520px] rounded-2xl border border-secondary bg-primary px-4 py-3 text-sm leading-6 text-primary shadow-xs">
                    {question}
                </div>
            </div>
        </div>
    );
});

AssistantMessage.displayName = "AssistantMessage";

export const OrderWizard = ({ onCancel }: OrderWizardProps) => {
    const { t } = useTranslation(['common', 'pages:orders']);
    const navigate = useNavigate();
    const { wizardFormData, setWizardFormData, appendMessage, closeDrawer } = useChatAI();
    const listRef = useRef<HTMLDivElement | null>(null);

    const [currentStep, setCurrentStep] = useState<Step>("shipperId");
    const [shipperOptions, setShipperOptions] = useState<Array<{ id: string; label: string }>>([]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const steps: Step[] = ["shipperId", "targetDate", "packingListRequested", "notes", "review"];

    useEffect(() => {
        const accountId = storage.getItem('accountId') || '2';
        const collaborations = collaborationService.getByReceiverId(accountId);
        const activeCollaborations = collaborations.filter(c => c.status === 'Active');
        const allAccounts = accountService.getAll();
        const shipperAccounts = allAccounts.filter(acc => acc.userType === 'SHIPPER');

        const options = activeCollaborations.map(collab => {
            const shipper = shipperAccounts.find(acc => acc.id === collab.shipperId);
            return shipper ? {
                id: shipper.id,
                label: shipper.legalName,
            } : null;
        }).filter(Boolean) as Array<{ id: string; label: string }>;

        setShipperOptions(options);
    }, []);

    useEffect(() => {
        if (wizardFormData[currentStep]) {
            setInputValue(String(wizardFormData[currentStep]));
        } else {
            setInputValue("");
        }
    }, [currentStep, wizardFormData]);

    useEffect(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    }, [currentStep]);

    const getStepQuestion = (step: Step): string => {
        switch (step) {
            case "shipperId":
                return t('aiChat.wizard.order.shipperId');
            case "targetDate":
                return t('aiChat.wizard.order.targetDate');
            case "packingListRequested":
                return t('aiChat.wizard.order.packingListRequested');
            case "notes":
                return t('aiChat.wizard.order.notes');
            default:
                return "";
        }
    };

    const handleAnswer = (value: string) => {
        const updatedData = { ...wizardFormData, [currentStep]: value };
        setWizardFormData(updatedData);

        appendMessage({ role: "user", content: value });

        setTimeout(() => {
            appendMessage({ role: "assistant", content: t('aiChat.wizard.confirmed') });

            const currentIndex = steps.indexOf(currentStep);
            if (currentIndex < steps.length - 1) {
                setTimeout(() => {
                    setCurrentStep(steps[currentIndex + 1]);
                    appendMessage({ role: "assistant", content: getStepQuestion(steps[currentIndex + 1]) });
                }, 500);
            } else {
                setTimeout(() => {
                    setCurrentStep("review");
                    showReview();
                }, 500);
            }
        }, 500);
    };

    const showReview = () => {
        const reviewMessage = t('aiChat.wizard.order.review', {
            shipper: shipperOptions.find(s => s.id === wizardFormData.shipperId)?.label,
            targetDate: wizardFormData.targetDate,
            packingList: wizardFormData.packingListRequested,
            notes: wizardFormData.notes || t('aiChat.wizard.notSpecified'),
        });
        appendMessage({ role: "assistant", content: reviewMessage });
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            const accountId = storage.getItem('accountId') || '2';

            if (!wizardFormData.shipperId || !wizardFormData.targetDate || !wizardFormData.packingListRequested?.trim()) {
                appendMessage({ role: "assistant", content: t('form.errors.allFieldsRequired', { ns: 'common' }) });
                setIsLoading(false);
                return;
            }

            let targetDate: Date;
            if (wizardFormData.targetDate instanceof CalendarDate) {
                targetDate = wizardFormData.targetDate.toDate(getLocalTimeZone());
            } else if (typeof wizardFormData.targetDate === 'string') {
                targetDate = new Date(wizardFormData.targetDate);
            } else {
                targetDate = new Date();
            }
            const targetWeek = getWeekNumber(targetDate);
            const targetYear = targetDate.getFullYear();

            const newOrder = orderService.create({
                receiverId: accountId,
                shipperId: wizardFormData.shipperId,
                targetWeek,
                targetYear,
                packingList: {
                    requested: wizardFormData.packingListRequested
                },
                notes: wizardFormData.notes || undefined
            });

            appendMessage({ role: "assistant", content: t('aiChat.wizard.order.success') });
            setCurrentStep("completed");

            setTimeout(() => {
                closeDrawer();
                navigate(`/receiver/orders/${newOrder.id}`);
            }, 2000);
        } catch (error) {
            appendMessage({ role: "assistant", content: t('form.errors.generic', { ns: 'common' }) });
        } finally {
            setIsLoading(false);
        }
    };

    const renderInput = () => {
        if (currentStep === "review") {
            return (
                <div className="space-y-3">
                    <Button onClick={handleSubmit} color="primary" size="lg" isDisabled={isLoading}>
                        {isLoading ? t('form.saving', { ns: 'common' }) : t('aiChat.wizard.create')}
                    </Button>
                    <Button onClick={onCancel} color="secondary" size="lg">
                        {t('buttons.cancel')}
                    </Button>
                </div>
            );
        }

        if (currentStep === "completed") {
            return (
                <div className="text-center text-primary">
                    {t('aiChat.wizard.order.completed')}
                </div>
            );
        }

        if (currentStep === "targetDate") {
            const currentDate = wizardFormData.targetDate
                ? (wizardFormData.targetDate instanceof CalendarDate
                    ? wizardFormData.targetDate
                    : new CalendarDate(new Date(wizardFormData.targetDate).getFullYear(), new Date(wizardFormData.targetDate).getMonth() + 1, new Date(wizardFormData.targetDate).getDate()))
                : null;

            return (
                <DatePicker
                    value={currentDate}
                    onChange={(date) => {
                        if (date) {
                            handleAnswer(date.toDate(getLocalTimeZone()).toISOString());
                        }
                    }}
                    minValue={today(getLocalTimeZone())}
                />
            );
        }

        if (currentStep === "shipperId") {
            return (
                <div className="space-y-2">
                    {shipperOptions.map((option) => (
                        <Button
                            key={option.id}
                            onClick={() => handleAnswer(option.id)}
                            color="secondary"
                            size="lg"
                            className="w-full"
                        >
                            {option.label}
                        </Button>
                    ))}
                </div>
            );
        }

        if (currentStep === "packingListRequested" || currentStep === "notes") {
            return (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (inputValue.trim() || currentStep === "notes") {
                            handleAnswer(inputValue.trim());
                            setInputValue("");
                        }
                    }}
                    className="space-y-3"
                >
                    <TextArea
                        value={inputValue}
                        onChange={setInputValue}
                        placeholder={t('aiChat.wizard.enterValue')}
                        rows={currentStep === "packingListRequested" ? 6 : 4}
                    />
                    <div className="flex gap-2">
                        <Button type="submit" color="primary" size="md">
                            {t('buttons.next')}
                        </Button>
                        <Button type="button" onClick={onCancel} color="secondary" size="md">
                            {t('buttons.cancel')}
                        </Button>
                    </div>
                </form>
            );
        }

        return (
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    if (inputValue.trim()) {
                        handleAnswer(inputValue.trim());
                        setInputValue("");
                    }
                }}
                className="space-y-3"
            >
                <Input
                    value={inputValue}
                    onChange={setInputValue}
                    placeholder={t('aiChat.wizard.enterValue')}
                    size="md"
                />
                <div className="flex gap-2">
                    <Button type="submit" color="primary" size="md">
                        {t('buttons.next')}
                    </Button>
                    <Button type="button" onClick={onCancel} color="secondary" size="md">
                        {t('buttons.cancel')}
                    </Button>
                </div>
            </form>
        );
    };

    // Memoizzare il componente avatar per evitare re-render inutili
    const AssistantAvatar = useMemo(() => {
        return <Avatar size="sm" src="/icons/ICO-ask-eve-hover.svg" status="online" className="mr-3" />;
    }, []);

    // Memoizzare la domanda dello step corrente per evitare re-render quando cambia solo inputValue
    const currentStepQuestion = useMemo(() => {
        return getStepQuestion(currentStep);
    }, [currentStep, t]);

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div ref={listRef} className="flex-1 overflow-y-auto py-4 md:py-6">
                <div className="flex flex-col gap-5">
                    <AssistantMessage
                        question={currentStepQuestion}
                        avatar={AssistantAvatar}
                    />

                    {renderInput()}
                </div>
            </div>
        </div>
    );
};

